/**
 * Per-tile freshness line (bottom-right overlay in `TileFrame`): hover-revealed
 * "Updated 2m ago · 45 ms" while the tile is healthy, ALWAYS visible in amber
 * ("Stale — updated 2m ago") while the runtime says the shown data is stale —
 * the trust model demands staleness be unmissable, while routine freshness
 * stays quiet chrome. Reads S3 runtime state only; no new plumbing.
 */
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { selectTileRuntime, useDashboardRuntimeStore } from '../../store/dashboardRuntimeStore';
import { formatRelativeTime, formatRunDuration } from './tileFreshness';

const TICK_MS = 10_000;

const STALE_REASON_COPY: Record<string, string> = {
  'connection-lost': 'Connection lost — showing the last loaded data.',
  'policy-blocked': 'Scheduled refresh is blocked for this query — showing the last loaded data.',
};

export function TileFreshnessFooter({
  dashboardId,
  tileId,
}: {
  dashboardId: string;
  tileId: string;
}) {
  const runtime = useDashboardRuntimeStore(selectTileRuntime(dashboardId, tileId));
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const last = runtime?.lastSuccess;
  if (!last) return null;

  const stale = runtime.status === 'stale';
  const relative = formatRelativeTime(last.at, nowMs);
  const duration = formatRunDuration(last.result.durationMs);
  const absolute = new Date(last.at).toLocaleString();
  const title = stale
    ? `${runtime.error ?? STALE_REASON_COPY[runtime.staleReason ?? ''] ?? 'Data is stale.'} Last updated ${absolute}.`
    : `Last updated ${absolute} · ran in ${duration}`;

  return (
    <span
      className={cn(
        'absolute bottom-1 right-1.5 z-10 select-none rounded-sm bg-card/85 px-1.5 py-0.5 text-[10px] leading-none',
        stale
          ? 'text-warning'
          : 'pointer-events-none text-muted-foreground opacity-0 transition-opacity group-hover/tile:opacity-100',
      )}
      title={title}
      data-testid="tile-freshness"
      data-stale={stale || undefined}
    >
      {stale ? `Stale — updated ${relative}` : `Updated ${relative} · ${duration}`}
    </span>
  );
}
